import { call, put, spawn, take, select } from 'redux-saga/effects';
import { Record } from 'immutable';
import { createSelector } from 'reselect';
import { appName } from '../config';
import api from '../services/alerts-service';

import {
  SET_ALERT_TO_FAVORITE as SET_ALERT_CATEGORY_TO_FAVORITE,
  DELETE_ALERT_FROM_FAVORITE as DELETE_ALERT_CATEGORY_FROM_FAVORITE,
} from './home-category-page';

import {
  SET_ALERT_TO_FAVORITE as SET_ALERT_THREAD_TO_FAVORITE,
  DELETE_ALERT_FROM_FAVORITE as DELETE_ALERT_THREAD_FROM_FAVORITE,
} from './thread-page';

/**
 * Constants
 */
export const moduleName = 'alert-favorite';
export const prefix = `${appName}/${moduleName}`;

export const ADD_REMOVE_FAVORITE_REQUEST = `${prefix}/ADD_REMOVE_FAVORITE_REQUEST`;

export const ADD_TO_FAVORITE_SUCCESS = `${prefix}/ADD_TO_FAVORITE_SUCCESS`;
export const ADD_TO_FAVORITE_FAIL = `${prefix}/ADD_TO_FAVORITE_FAIL`;

export const REMOVE_FROM_FAVORITE_SUCCESS = `${prefix}/REMOVE_FROM_FAVORITE_SUCCESS`;
export const REMOVE_FROM_FAVORITE_FAIL = `${prefix}/REMOVE_FROM_FAVORITE_FAIL`;

// constants for all favorite types (author, alert, category, thread)
export const FAVORITE_RESET_TO_DEFAULT = 'FAVORITE_RESET_TO_DEFAULT';
export const FAVORITE_STATE_CLEAN_UP = 'FAVORITE_STATE_CLEAN_UP'; // saved loading state for link spinner

/**
 * Reducer
 */

export const ProcessRecord = Record({
  done: false,
  err: null,
});

export const ReducerRecord = Record({
  loading: false,
  added: ProcessRecord(),
  removed: ProcessRecord(),
});

export default function reducer(state = ReducerRecord(), { type, _, err }) {
  switch (type) {
    case ADD_REMOVE_FAVORITE_REQUEST:
      return state.set('loading', true);

    case ADD_TO_FAVORITE_SUCCESS:
      return state
        .set('loading', false)
        .set('removed', ProcessRecord())
        .set('added', ProcessRecord({ done: true }));

    case ADD_TO_FAVORITE_FAIL:
      return state
        .set('loading', false)
        .set('removed', ProcessRecord())
        .set('added', ProcessRecord({ err }));

    case REMOVE_FROM_FAVORITE_SUCCESS:
      return state
        .set('loading', false)
        .set('removed', ProcessRecord({ done: true }))
        .set('added', ProcessRecord());

    case REMOVE_FROM_FAVORITE_FAIL:
      return state
        .set('loading', false)
        .set('removed', ProcessRecord({ err }))
        .set('added', ProcessRecord());

    case FAVORITE_STATE_CLEAN_UP:
      return state
        .set('removed', ProcessRecord())
        .set('added', ProcessRecord());

    case FAVORITE_RESET_TO_DEFAULT:
      return state.clear();

    default:
      return state;
  }
}

/**
 * Actions
 */

// isThread, isFavorite - params for checking which post (board or thread) and state
export const addRemoveAlertFavorite = (path, isThread, isFavorite) => ({
  type: ADD_REMOVE_FAVORITE_REQUEST,
  payload: { path, isThread, isFavorite },
});
export const resetAlertFavoriteState = () => ({
  type: FAVORITE_RESET_TO_DEFAULT,
});

/**
 * Selectors
 */

export const stateSelector = (state) => state[moduleName];
export const loadingAlertSelector = createSelector(
  stateSelector,
  (state) => state.loading
);

export const addedAlertSuccessSelector = createSelector(
  stateSelector,
  (state) => state.added.done
);

export const addedAlertFailSelector = createSelector(
  stateSelector,
  (state) => state.added.err
);

export const removedAlertSuccessSelector = createSelector(
  stateSelector,
  (state) => state.removed.done
);

export const removedAlertFailSelector = createSelector(
  stateSelector,
  (state) => state.removed.err
);

/**
 * Sagas
 */

const addAlertToFavoriteSaga = function*(path, isThread) {
  try {
    yield call(api.createAlert, path);
    yield put({ type: ADD_TO_FAVORITE_SUCCESS });

    if (isThread) yield put({ type: SET_ALERT_THREAD_TO_FAVORITE });
    else yield put({ type: SET_ALERT_CATEGORY_TO_FAVORITE });
  } catch (err) {
    yield put({ type: ADD_TO_FAVORITE_FAIL, err });
  }
};

const removeAlertFromFavoriteSaga = function*(path, isThread) {
  try {
    yield call(api.deleteAlert, path);
    yield put({ type: REMOVE_FROM_FAVORITE_SUCCESS });

    if (isThread) yield put({ type: DELETE_ALERT_THREAD_FROM_FAVORITE });
    else yield put({ type: DELETE_ALERT_CATEGORY_FROM_FAVORITE });
  } catch (err) {
    yield put({ type: REMOVE_FROM_FAVORITE_FAIL, err });
  }
};

export const addRemoveAlertSaga = function*() {
  while (true) {
    const {
      payload: { path, isThread, isFavorite },
    } = yield take(ADD_REMOVE_FAVORITE_REQUEST);
    yield put({ type: FAVORITE_STATE_CLEAN_UP }); // close-clean all notice favorites

    if (isFavorite) yield call(removeAlertFromFavoriteSaga, path, isThread);
    else yield call(addAlertToFavoriteSaga, path, isThread);
  }
};

export const saga = function*() {
  yield spawn(addRemoveAlertSaga);
};
